import React, { useState, useEffect } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_TI } from '../../shared/styles';
import { getPulse, createPulse, respondPulse, getChallenges, createChallenge, deleteChallenge } from './businessApi';

const WEEK_START = (() => {
  const d = new Date();
  const day = d.getDay(); // 0=Sun
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
})();

const METRICS = [
  { id: 'sleep', icon: '😴', nl: 'Slaap (uur)', en: 'Sleep (hrs)', max: 10 },
  { id: 'mood',  icon: '😊', nl: 'Humeur (1-5)',  en: 'Mood (1-5)',  max: 5 },
];

export default function BusinessPulseScreen({ lang, businessId, currentUserId, isOwner }) {
  const nl = lang === 'nl';
  const [tab, setTab] = useState('pulse');

  // Pulse
  const [pulses, setPulses] = useState([]);
  const [loadingPulse, setLoadingPulse] = useState(true);
  const [newQ, setNewQ] = useState('');
  const [newOpts, setNewOpts] = useState('');
  const [creating, setCreating] = useState(false);
  const [answered, setAnswered] = useState({}); // pulseId -> true (merged from server + local)
  const [answerInput, setAnswerInput] = useState({});

  // Challenges
  const [challenges, setChallenges] = useState([]);
  const [loadingCh, setLoadingCh] = useState(true);
  const [newCh, setNewCh] = useState({ title: '', description: '', metric: 'sleep', target_value: 7 });
  const [creatingCh, setCreatingCh] = useState(false);

  const loadPulse = () => {
    setLoadingPulse(true);
    getPulse(businessId, currentUserId).then(d => {
      const list = Array.isArray(d) ? d : [];
      setPulses(list);
      // Seed answered from server-returned user_responded flag
      const fromServer = {};
      list.forEach(p => { if (p.user_responded) fromServer[p.id] = true; });
      setAnswered(prev => ({ ...prev, ...fromServer }));
      setLoadingPulse(false);
    }).catch(() => setLoadingPulse(false));
  };
  const loadChallenges = () => {
    setLoadingCh(true);
    getChallenges(businessId, WEEK_START).then(d => { setChallenges(Array.isArray(d) ? d : []); setLoadingCh(false); }).catch(() => setLoadingCh(false));
  };

  useEffect(() => { loadPulse(); loadChallenges(); }, [businessId]); // eslint-disable-line

  const handleCreatePulse = async () => {
    if (!newQ.trim()) return;
    setCreating(true);
    const opts = newOpts.trim() ? newOpts.split(',').map(s => s.trim()).filter(Boolean) : null;
    await createPulse(businessId, { question: newQ, options: opts, created_by: currentUserId });
    setNewQ(''); setNewOpts('');
    setCreating(false);
    loadPulse();
  };

  const handleAnswer = async (pulseId) => {
    const ans = answerInput[pulseId]?.trim();
    if (!ans) return;
    await respondPulse(businessId, pulseId, { answer: ans, user_id: currentUserId });
    setAnswered(p => ({ ...p, [pulseId]: true }));
    loadPulse();
  };

  const handleCreateChallenge = async () => {
    if (!newCh.title.trim()) return;
    setCreatingCh(true);
    await createChallenge(businessId, { ...newCh, week_start: WEEK_START });
    setNewCh({ title: '', description: '', metric: 'sleep', target_value: 7 });
    setCreatingCh(false);
    loadChallenges();
  };

  const handleDeleteChallenge = async (cid) => {
    await deleteChallenge(businessId, cid);
    loadChallenges();
  };

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>

      {/* Sub-tab strip */}
      <div style={{ display: 'flex', background: _T.bg4, borderRadius: 14, padding: 4, marginBottom: 16, gap: 4 }}>
        {[['pulse', nl ? '📊 Pulse' : '📊 Pulse'], ['challenges', nl ? '🏆 Challenges' : '🏆 Challenges']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ flex: 1, background: tab === id ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : 'transparent', border: 'none', borderRadius: 10, color: tab === id ? '#0a1220' : C.silver, fontSize: 12, fontWeight: 700, padding: '9px 4px', cursor: 'pointer', transition: 'all 0.15s' }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'pulse' && (
        <>
          {/* Feature 3: Anonymous Team Pulse */}
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'ANONIEME TEAM PULSE' : 'ANONYMOUS TEAM PULSE'}</div>

          {isOwner && (
            <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: C.silver, marginBottom: 8 }}>{nl ? 'Nieuwe vraag versturen aan het team' : 'Send new question to team'}</div>
              <input value={newQ} onChange={e => setNewQ(e.target.value)}
                placeholder={nl ? 'Bijv: Hoe voel je je deze week?' : 'E.g. How do you feel this week?'}
                style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 13, marginBottom: 8 }} />
              <input value={newOpts} onChange={e => setNewOpts(e.target.value)}
                placeholder={nl ? 'Opties (komma-gescheiden, optioneel)' : 'Options (comma-separated, optional)'}
                style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 12, marginBottom: 10 }} />
              <button onClick={handleCreatePulse} disabled={creating || !newQ.trim()}
                style={{ background: creating ? `${_T.accent}66` : `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 13, fontWeight: 700, padding: '10px 20px', cursor: creating ? 'default' : 'pointer', width: '100%' }}>
                {creating ? '...' : (nl ? 'Verstuur naar team' : 'Send to team')}
              </button>
            </div>
          )}

          {loadingPulse ? (
            <div style={{ color: C.silver, textAlign: 'center', padding: 24, fontSize: 13 }}>...</div>
          ) : pulses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: C.silver, fontSize: 13 }}>
              {nl ? 'Nog geen pulse-vragen. Stuur er een!' : 'No pulse questions yet. Send one!'}
            </div>
          ) : pulses.map(p => {
            const totalResp = p.responses?.reduce((s, r) => s + (parseInt(r.cnt) || 0), 0) || 0;
            const isDone = !!answered[p.id];
            const opts = p.options || [];
            return (
              <div key={p.id} style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ color: C.white, fontSize: 14, fontWeight: 600, flex: 1, lineHeight: 1.4 }}>{p.question}</div>
                  {p.active ? <span style={{ fontSize: 8, color: C.teal, background: `${C.teal}15`, border: `1px solid ${C.teal}33`, borderRadius: 6, padding: '2px 6px', marginLeft: 8, flexShrink: 0 }}>LIVE</span>
                    : <span style={{ fontSize: 8, color: C.silver, marginLeft: 8, flexShrink: 0 }}>CLOSED</span>}
                </div>

                {/* Responses breakdown (owner sees) */}
                {isOwner && p.responses && p.responses.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: C.silver, marginBottom: 6 }}>{totalResp} {nl ? 'reacties' : 'responses'}</div>
                    {p.responses.map(r => {
                      const pct = totalResp > 0 ? Math.round((r.cnt / totalResp) * 100) : 0;
                      return (
                        <div key={r.answer} style={{ marginBottom: 6 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <span style={{ color: C.silver, fontSize: 11 }}>{r.answer}</span>
                            <span style={{ color: C.white, fontSize: 11, fontWeight: 700 }}>{r.cnt} ({pct}%)</span>
                          </div>
                          <div style={{ height: 4, background: `${_T.accent}20`, borderRadius: 2 }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg,${_T.grad1},${_T.grad2})`, borderRadius: 2, transition: 'width 0.5s' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Answer input (members + owner) */}
                {p.active && !isDone && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    {opts.length > 0 ? (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
                        {opts.map(opt => (
                          <button key={opt} onClick={() => { setAnswerInput(prev => ({ ...prev, [p.id]: opt })); }}
                            style={{ background: answerInput[p.id] === opt ? `${_T.accent}25` : _T.bg4, border: `1px solid ${answerInput[p.id] === opt ? _T.accent : _T.cardBorderBot}`, borderRadius: 10, color: answerInput[p.id] === opt ? _T.accent : C.silver, fontSize: 12, padding: '7px 12px', cursor: 'pointer' }}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <input value={answerInput[p.id] || ''} onChange={e => setAnswerInput(prev => ({ ...prev, [p.id]: e.target.value }))}
                        placeholder={nl ? 'Jouw anonieme antwoord...' : 'Your anonymous answer...'}
                        style={{ ...s_TI({ flex: 1 }), fontSize: 12 }} />
                    )}
                    <button onClick={() => handleAnswer(p.id)} disabled={!answerInput[p.id]}
                      style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 10, color: '#0a1220', fontSize: 12, fontWeight: 700, padding: '0 14px', cursor: !answerInput[p.id] ? 'default' : 'pointer', flexShrink: 0 }}>
                      {nl ? 'Stuur' : 'Send'}
                    </button>
                  </div>
                )}
                {isDone && (
                  <div style={{ color: C.teal, fontSize: 12, marginTop: 6 }}>✓ {nl ? 'Reactie verstuurd (anoniem)' : 'Response sent (anonymous)'}</div>
                )}
              </div>
            );
          })}
        </>
      )}

      {tab === 'challenges' && (
        <>
          {/* Feature 6: Team Challenges */}
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'TEAM CHALLENGE DEZE WEEK' : 'TEAM CHALLENGE THIS WEEK'}</div>

          {isOwner && (
            <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: C.silver, marginBottom: 10 }}>{nl ? 'Nieuwe challenge aanmaken' : 'Create new challenge'}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input value={newCh.title} onChange={e => setNewCh(p => ({ ...p, title: e.target.value }))}
                  placeholder={nl ? 'Challenge naam' : 'Challenge name'}
                  style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 13 }} />
                <input value={newCh.description} onChange={e => setNewCh(p => ({ ...p, description: e.target.value }))}
                  placeholder={nl ? 'Beschrijving (optioneel)' : 'Description (optional)'}
                  style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 12 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: C.silver, marginBottom: 4 }}>{nl ? 'Metric' : 'Metric'}</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {METRICS.map(m => (
                        <button key={m.id} onClick={() => setNewCh(p => ({ ...p, metric: m.id }))}
                          style={{ flex: 1, background: newCh.metric === m.id ? `${_T.accent}20` : _T.bg4, border: `1px solid ${newCh.metric === m.id ? _T.accent : _T.cardBorderBot}`, borderRadius: 10, color: newCh.metric === m.id ? _T.accent : C.silver, fontSize: 11, padding: '8px 4px', cursor: 'pointer' }}>
                          {m.icon} {nl ? m.nl : m.en}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ width: 90 }}>
                    <div style={{ fontSize: 10, color: C.silver, marginBottom: 4 }}>{nl ? 'Doel' : 'Target'}</div>
                    <input type="number" value={newCh.target_value} onChange={e => setNewCh(p => ({ ...p, target_value: parseFloat(e.target.value) || 7 }))} min="1" max={METRICS.find(m => m.id === newCh.metric)?.max || 10} step="0.5"
                      style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 13 }} />
                  </div>
                </div>
                <button onClick={handleCreateChallenge} disabled={creatingCh || !newCh.title.trim()}
                  style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 13, fontWeight: 700, padding: '11px', cursor: creatingCh ? 'default' : 'pointer' }}>
                  {creatingCh ? '...' : (nl ? '+ Aanmaken' : '+ Create')}
                </button>
              </div>
            </div>
          )}

          {loadingCh ? (
            <div style={{ color: C.silver, textAlign: 'center', padding: 24 }}>...</div>
          ) : challenges.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: C.silver, fontSize: 13 }}>
              {nl ? 'Geen challenges deze week.' : 'No challenges this week.'}
            </div>
          ) : challenges.map(ch => {
            const metric = METRICS.find(m => m.id === ch.metric) || METRICS[0];
            const progress = ch.progress || [];
            const achieved = progress.filter(p => parseFloat(p.avg_val) >= ch.target_value);
            return (
              <div key={ch.id} style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>{ch.title}</div>
                    {ch.description && <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>{ch.description}</div>}
                  </div>
                  {isOwner && (
                    <button onClick={() => handleDeleteChallenge(ch.id)}
                      style={{ background: 'none', border: 'none', color: `${C.red}66`, cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 20 }}>{metric.icon}</span>
                  <div>
                    <div style={{ color: C.silver, fontSize: 10 }}>{nl ? 'Doel' : 'Target'}</div>
                    <div style={{ color: _T.accent, fontSize: 16, fontWeight: 700 }}>{ch.target_value} {nl ? metric.nl : metric.en}</div>
                  </div>
                  <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                    <div style={{ color: C.silver, fontSize: 10 }}>{nl ? 'Gehaald door' : 'Achieved by'}</div>
                    <div style={{ color: C.teal, fontSize: 16, fontWeight: 700 }}>{achieved.length}/{progress.length}</div>
                  </div>
                </div>
                {/* Progress leaderboard */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {progress.sort((a, b) => (parseFloat(b.avg_val) || 0) - (parseFloat(a.avg_val) || 0)).map((p, i) => {
                    const val = parseFloat(p.avg_val) || 0;
                    const pct = Math.min(100, (val / (metric.max || ch.target_value)) * 100);
                    const done = val >= ch.target_value;
                    return (
                      <div key={p.user_id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                          <span style={{ color: C.silver, fontSize: 11 }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '  '} {p.name || 'Member'}</span>
                          <span style={{ color: done ? C.teal : C.white, fontSize: 11, fontWeight: 700 }}>{val.toFixed(1)} {done ? '✓' : ''}</span>
                        </div>
                        <div style={{ height: 4, background: `${_T.accent}15`, borderRadius: 2 }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: done ? `linear-gradient(90deg,${C.teal},${_T.accent})` : `linear-gradient(90deg,${_T.grad1},${_T.grad2})`, borderRadius: 2, transition: 'width 0.6s' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
